<script setup>
import { useRoute } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import { useAuth } from '@/composables/useAuth'
import { accountNavItem, isNavActive, railNavItems } from '@/navigation'

const route = useRoute()
const { isAuthenticated } = useAuth()

function isActive(item) {
  return isNavActive(route.path, item)
}
</script>

<template>
  <aside class="rail" :class="{ 'rail--signed-out': !isAuthenticated }" aria-label="Primary">
    <RouterLink class="rail-brand" to="/home">
      <img class="rail-mark" src="/icon-512.png" alt="" width="36" height="36" />
      <span>GMSomeday</span>
    </RouterLink>

    <nav class="rail-nav">
      <RouterLink
        v-for="item in railNavItems"
        :key="item.label"
        :to="item.to"
        class="rail-link"
        :class="{ 'is-active': isActive(item) }"
      >
        <span class="rail-icon">
          <AppIcon :paths="item.icon" />
        </span>
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>

    <nav class="rail-nav rail-nav--secondary">
      <RouterLink class="rail-link" :class="{ 'is-active': isActive(accountNavItem) }" :to="accountNavItem.to">
        <span class="rail-icon">
          <AppIcon :paths="accountNavItem.icon" />
        </span>
        <span>{{ accountNavItem.label }}</span>
      </RouterLink>
    </nav>

    <RouterLink v-if="!isAuthenticated" class="rail-cta" to="/login?next=/home">
      Sign in
    </RouterLink>
  </aside>
</template>

<style scoped>
.rail {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  display: none;
  flex-direction: column;
  width: 232px;
  border-right: 1px solid var(--hairline-strong);
  background: var(--rail-bg);
  padding: var(--space-lg) 12px;
}
.rail-brand {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  min-height: 40px;
  padding: 0 var(--space-sm);
  color: var(--ink);
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
}
.rail-mark {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  object-fit: contain;
}
.rail-nav {
  display: grid;
  gap: 6px;
  margin-top: var(--space-xl);
}
.rail-nav--secondary {
  margin-top: auto;
  border-top: 1px solid var(--hairline);
  padding-top: var(--space-md);
}
.rail--signed-out .rail-nav--secondary {
  margin-bottom: 68px;
}
.rail-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  border-radius: var(--radius-md);
  color: var(--muted);
  font-size: 15px;
  font-weight: 520;
  line-height: 1;
  text-decoration: none;
  padding: 0 10px;
  transition:
    background-color 140ms ease,
    color 140ms ease,
    box-shadow 140ms ease,
    transform 140ms ease;
}
.rail-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  color: color-mix(in srgb, var(--ink-gold) 72%, var(--muted));
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    color 140ms ease,
    transform 140ms ease;
}
.rail-icon svg {
  width: 18px;
  height: 18px;
}
.rail-link::before {
  position: absolute;
  top: 11px;
  bottom: 11px;
  left: 0;
  width: 3px;
  border-radius: var(--radius-full);
  background: transparent;
  content: "";
}
.rail-link:hover {
  background: rgba(var(--ink-rgb), 0.035);
  color: var(--ink);
  transform: translateX(2px);
}
.rail-link:hover .rail-icon {
  background: rgba(var(--gold-rgb), 0.08);
  color: var(--ink-gold);
}
.rail-link.is-active {
  background: rgba(var(--gold-rgb), 0.10);
  color: var(--ink);
  font-weight: 600;
  box-shadow: inset 0 0 0 1px rgba(var(--gold-rgb), 0.14);
}
.rail-link.is-active::before {
  background: var(--someday-gold);
}
.rail-link.is-active .rail-icon {
  border-color: rgba(var(--gold-rgb), 0.22);
  background: var(--someday-gold);
  color: var(--paper);
  transform: translateX(1px);
}
.rail-cta {
  position: absolute;
  right: 12px;
  bottom: var(--space-lg);
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: var(--radius-md);
  background: var(--ink);
  color: var(--paper);
  font-weight: 700;
  text-decoration: none;
  transition:
    background-color var(--motion-fast) var(--ease-out),
    box-shadow var(--motion-fast) var(--ease-out),
    transform var(--motion-fast) var(--ease-press);
}
.rail-cta:hover {
  background: var(--neutral-800);
  box-shadow: 0 10px 22px rgba(var(--ink-rgb), 0.16);
  transform: translateY(-1px);
}
.rail-cta:active {
  box-shadow: none;
  transform: translateY(1px) scale(0.99);
}
@media (min-width: 768px) {
  .rail { display: flex; }
}
</style>
